import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Download, Lock, Sparkles, Video as VideoIcon, Share2 } from "lucide-react";
import { publicPass, checkoutMediaUpsell, api } from "@/lib/api";

/**
 * VELORA JDM — Boarding Pass pubblico + galleria media.
 * URL: /pass/:voucher
 * - Se il cliente ha comprato addon foto/video/bundle: media in HD scaricabili
 * - Altrimenti: media con watermark VELORA + CTA Stripe upsell (€10/€20/€25)
 */
export default function Pass() {
    const { voucher } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [buyingProduct, setBuyingProduct] = useState(null);
    const [mediaProducts, setMediaProducts] = useState([]);
    const [searchParams] = useSearchParams();

    useEffect(() => {
        publicPass(voucher)
            .then(setData)
            .catch(e => setError(e?.response?.status === 404 ? "Voucher non trovato" : "Errore caricamento"))
            .finally(() => setLoading(false));
    }, [voucher]);

    useEffect(() => {
        api.get("/media-products").then(r => setMediaProducts(r.data.items || [])).catch(() => {});
    }, []);

    useEffect(() => {
        if (searchParams.get("upsell") === "ok") toast.success("Pagamento ricevuto! I file HD sono ora disponibili senza watermark.");
        if (searchParams.get("upsell") === "cancel") toast.info("Pagamento annullato.");
    }, [searchParams]);

    const buy = async (product) => {
        setBuyingProduct(product);
        try {
            const r = await checkoutMediaUpsell(voucher, product, window.location.origin);
            window.location.href = r.url;
        } catch { toast.error("Errore avvio pagamento"); setBuyingProduct(null); }
    };

    if (loading) return <div className="min-h-screen bg-[#15181C] text-[#F4F2ED] flex items-center justify-center">Caricamento…</div>;
    if (error) return (
        <div className="min-h-screen bg-[#15181C] text-[#F4F2ED] flex items-center justify-center px-5">
            <div className="text-center">
                <div className="font-display text-5xl uppercase mb-3">Errore</div>
                <div className="text-[#9AA2AD]">{error}</div>
                <a href="/" className="mt-6 inline-block border border-[#E2231A] px-6 py-3 uppercase text-xs tracking-widest hover:bg-[#E2231A] transition-colors duration-150">Torna al sito</a>
            </div>
        </div>
    );

    const total = data.photos_count + data.videos_count;

    return (
        <div className="min-h-screen bg-[#15181C] text-[#F4F2ED]">
            {/* Boarding-pass style header */}
            <div className="bg-gradient-to-b from-[#1C2026] to-[#15181C] border-b-4 border-[#E2231A]">
                <div className="max-w-3xl mx-auto px-5 py-8 md:py-12">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-3 h-3 rounded-full bg-[#E2231A]" />
                        <div className="font-display text-2xl tracking-[0.3em]">VELORA JDM</div>
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.4em] text-[#9AA2AD] mb-2">Boarding Pass</div>

                    <div className="grid md:grid-cols-3 gap-6 items-center">
                        <div className="md:col-span-2">
                            <div className="flex items-baseline gap-4 flex-wrap">
                                <div className="font-display text-6xl md:text-8xl text-[#E2231A]">#{data.turno_progressivo}</div>
                                <div>
                                    <div className="font-display text-3xl md:text-4xl uppercase">{data.customer_first_name}</div>
                                    <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD] mt-1">{data.voucher_code} · {data.product_id}</div>
                                </div>
                            </div>
                            <div className="mt-6 grid grid-cols-2 gap-3 text-[10px] uppercase tracking-widest text-[#9AA2AD]">
                                <div>
                                    <div>Auto</div>
                                    <div className={`font-display text-2xl mt-1 ${data.assigned_car === "EVO" ? "text-[#E2231A]" : data.assigned_car === "WRX" ? "text-[#4B7BEA]" : "text-[#F4F2ED]"}`}>{data.assigned_car || "—"}</div>
                                </div>
                                <div>
                                    <div>Ora prevista</div>
                                    <div className="font-display text-2xl text-[#F4F2ED] mt-1">{data.planned_time || "—"}</div>
                                </div>
                                <div>
                                    <div>Data evento</div>
                                    <div className="font-display text-xl text-[#F4F2ED] mt-1">13.09.26</div>
                                </div>
                                <div>
                                    <div>Circuito</div>
                                    <div className="font-display text-xl text-[#F4F2ED] mt-1">del Sele</div>
                                </div>
                            </div>
                        </div>
                        {/* QR Boarding Pass */}
                        <div className="flex flex-col items-center bg-[#F4F2ED] p-4 md:p-6 md:justify-self-end" data-testid="pass-qr">
                            <img
                                src={`${process.env.REACT_APP_BACKEND_URL}/api/public/pass/${encodeURIComponent(data.voucher_code)}/qr.png`}
                                alt={`QR ${data.voucher_code}`}
                                className="w-40 h-40 md:w-48 md:h-48"
                                loading="lazy"
                            />
                            <div className="text-[9px] uppercase tracking-widest text-[#15181C] mt-2 font-display">Scan al gate</div>
                            <div className="text-[10px] text-[#15181C]/70 font-mono">{data.voucher_code}</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Gallery */}
            <div className="max-w-5xl mx-auto px-5 py-10">
                {total === 0 ? (
                    <div className="border border-[#9AA2AD]/20 p-16 text-center">
                        <div className="text-[#9AA2AD] text-lg mb-2">📸 I tuoi ricordi sono in lavorazione</div>
                        <div className="text-[#9AA2AD] text-xs uppercase tracking-widest">Torna dopo l'evento — riceverai un'email non appena saranno online.</div>
                    </div>
                ) : (
                    <>
                        {!data.has_purchased && (
                            <UpsellBanner buy={buy} buying={buyingProduct} products={mediaProducts} />
                        )}
                        <h2 className="font-display text-3xl uppercase mb-4 mt-6 flex items-center gap-3">
                            📸 Le tue foto <span className="text-[#9AA2AD] text-lg">· {data.photos_count}</span>
                        </h2>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-10">
                            {data.photos.map((p, i) => (
                                <MediaItem key={p.public_id} url={p.secure_url} kind="photo" locked={!data.has_purchased} />
                            ))}
                            {data.photos.length === 0 && <div className="col-span-3 text-[#9AA2AD] italic text-sm">Nessuna foto ancora.</div>}
                        </div>

                        <h2 className="font-display text-3xl uppercase mb-4 flex items-center gap-3">
                            🎥 I tuoi video <span className="text-[#9AA2AD] text-lg">· {data.videos_count}</span>
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {data.videos.map((v, i) => (
                                <MediaItem key={v.public_id} url={v.secure_url} kind="video" locked={!data.has_purchased} />
                            ))}
                            {data.videos.length === 0 && <div className="col-span-2 text-[#9AA2AD] italic text-sm">Nessun video ancora.</div>}
                        </div>

                        {data.has_purchased && (
                            <div className="mt-10 p-6 border border-[#16a34a]/40 bg-[#16a34a]/5 text-center">
                                <Sparkles className="mx-auto mb-3 text-[#16a34a]" />
                                <div className="font-display text-2xl uppercase mb-2">Grazie per essere stato con noi 🏁</div>
                                <div className="text-sm text-[#9AA2AD]">Se ci tagghi <b className="text-[#F4F2ED]">@velorajdm</b> quando pubblichi, ci fai un favore enorme.</div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

function MediaItem({ url, kind, locked }) {
    const download = () => {
        // Cloudinary attachment flag for forced download
        const dlUrl = url.includes("/upload/") ? url.replace("/upload/", "/upload/fl_attachment/") : url;
        window.open(dlUrl, "_blank");
    };
    return (
        <div className="relative group border border-[#9AA2AD]/20 bg-[#1C2026] overflow-hidden">
            {kind === "photo" ? (
                <img src={url} alt="" className="w-full aspect-[4/3] object-cover" loading="lazy" />
            ) : (
                <video src={url} controls preload="metadata" className="w-full aspect-video object-cover bg-black" />
            )}
            {!locked && kind === "photo" && (
                <button
                    onClick={download}
                    className="absolute bottom-2 right-2 bg-[#E2231A] hover:bg-[#C11B15] text-[#F4F2ED] p-2 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Scarica HD"
                ><Download size={14} /></button>
            )}
            {locked && (
                <div className="absolute top-2 left-2 bg-[#15181C]/80 backdrop-blur px-2 py-1 text-[10px] uppercase tracking-widest text-[#f59e0b] flex items-center gap-1">
                    <Lock size={10} /> Anteprima
                </div>
            )}
        </div>
    );
}

function UpsellBanner({ buy, buying, products = [] }) {
    return (
        <div className="border-2 border-[#E2231A] bg-gradient-to-br from-[#E2231A]/10 to-[#1C2026] p-6 md:p-8" data-testid="upsell-banner">
            <div className="flex items-start gap-3 flex-wrap">
                <Sparkles className="text-[#E2231A] flex-shrink-0 mt-1" />
                <div className="flex-1 min-w-0">
                    <h3 className="font-display text-2xl md:text-3xl uppercase mb-2">Sblocca i tuoi ricordi HD</h3>
                    <p className="text-sm text-[#9AA2AD] mb-5">Rimuovi il logo, scarica in alta risoluzione, condividi sui social.</p>
                    <div className="grid md:grid-cols-3 gap-3">
                        {products.length === 0 ? (
                            <div className="col-span-3 text-center text-[#9AA2AD] text-xs uppercase tracking-widest py-6">Caricamento…</div>
                        ) : products.map(p => (
                            <UpsellCard
                                key={p.id}
                                title={p.title}
                                price={`€${p.price}`}
                                desc={(p.features && p.features[0]) || ""}
                                product={p.id}
                                featured={!!p.featured}
                                testid={`buy-${p.id.replace("_", "-")}`}
                                buy={buy} buying={buying}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

function UpsellCard({ title, price, desc, product, featured, testid, buy, buying }) {
    return (
        <div className={`border ${featured ? "border-[#E2231A] bg-[#E2231A]/5" : "border-[#9AA2AD]/30 bg-[#15181C]"} p-4 flex flex-col`}>
            {featured && <div className="text-[9px] uppercase tracking-widest text-[#E2231A] mb-2">★ Miglior scelta</div>}
            <div className="font-display text-xl uppercase">{title}</div>
            <div className="font-display text-4xl mt-1 mb-1">{price}</div>
            <div className="text-[10px] uppercase tracking-widest text-[#9AA2AD] mb-4">{desc}</div>
            <button
                data-testid={testid}
                onClick={() => buy(product)}
                disabled={!!buying}
                className={`mt-auto uppercase tracking-widest text-xs px-4 py-2.5 font-display transition-colors duration-150 disabled:opacity-50 ${featured ? "bg-[#E2231A] hover:bg-[#C11B15] text-[#F4F2ED]" : "border border-[#F4F2ED]/30 hover:bg-[#F4F2ED] hover:text-[#15181C]"}`}
            >{buying === product ? "Attendere…" : "Acquista"}</button>
        </div>
    );
}
